import { Bot, Users, Cpu, Play, Save, Loader2 } from 'lucide-react';
import { useState } from 'react';
import Toast from '../Toast';
import workflowService from '../../services/workflowService';
import { deepEqualArray } from '../../helpers/common';

export const NodeToolbar = ({ onAddNode, nodesData, edgesData, workflowId, isEdit, setSelectedNode }) => {
  const [loading, setLoading] = useState(false);

  function generateSteps(nodes, edges) {
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

    let steps = [];

    const startNode = nodes.find(n => n.data.type === 'start' || n.id === 'start-1');
    if (!startNode) return steps;

    let currentNodeId = startNode.id;

    while (true) {
      const nextEdge = edges.find(e => e.source === currentNodeId);
      if (!nextEdge) break;

      const nextNode = nodeMap[nextEdge.target];
      if (!nextNode) break;

      const stepData = nextNode.data;

      let formattedStep = {
        type: stepData?.type,
        step_name: stepData?.label || '',
        step_description: stepData?.instructions || stepData?.step_description || '',
      };

      if (stepData.type === 'agent') {
        if (typeof stepData?.agentId === 'object') {
          formattedStep.agent_id = stepData?.agentId?.value || '';
        } else {
          formattedStep.agent_id = stepData?.agent_id || stepData?.agentId || '';
        }
      }

      if (stepData.type === 'team') {
        if (typeof stepData?.teamId === 'object') {
          formattedStep.team_id = stepData?.teamId?.value || '';
        } else {
          formattedStep.team_id = stepData?.team_id || stepData?.teamId || '';
        }
      }

      if (stepData.type === 'llm') {
        if (typeof stepData?.model === 'object') {
          formattedStep.model = stepData?.model?.value || '';
        } else {
          formattedStep.model = stepData?.model || '';
        }
        formattedStep.prompt = stepData?.prompt || '';
      }

      steps.push(formattedStep);
      currentNodeId = nextNode.id;
    }

    return steps;
  }

  const handlePreview = async () => {
    setLoading(true);
    try {
      const steps = generateSteps(nodesData, edgesData);

      if (deepEqualArray(isEdit?.steps, steps)) {
        Toast({
          type: 'error',
          message: 'No changes found!',
        });
      } else {
        const editPayload = {
          steps,
          workflow_id: workflowId,
        };
        const res = await workflowService.updateWorkflow(editPayload);
        Toast({
          type: 'success',
          message: res.detail || 'Workflow updated successfully!',
        });
      }
      setLoading(false);
    } catch (error) {
      Toast({
        type: 'error',
        message: error.message || 'Something went wrong!',
      });
      setLoading(false);
    }
  };

  const toolButtonClass =
    'flex flex-col items-center gap-1 px-3 py-2 rounded-md text-xs text-mute hover:text-ink hover:bg-paper transition-colors disabled:opacity-50';

  return (
    <div className="absolute top-4 right-4 z-10">
      <div className="bg-white border border-shell-border rounded-lg p-1.5 shadow-lg flex gap-1">
        <button type="button" onClick={handlePreview} disabled={loading} className={toolButtonClass}>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <Save className="h-5 w-5 text-primary" />
          )}
          <span>Save</span>
        </button>

        <button
          type="button"
          onClick={() =>
            setSelectedNode({
              data: {
                type: 'chat',
                label: 'Chat',
              },
            })
          }
          disabled={loading}
          className={toolButtonClass}>
          <Play className="h-5 w-5" />
          <span>Run</span>
        </button>

        <div className="w-px bg-shell-border my-1" />

        <button type="button" onClick={() => onAddNode('agent')} className={toolButtonClass}>
          <Bot className="h-5 w-5" />
          <span>Agent</span>
        </button>

        <button type="button" onClick={() => onAddNode('team')} className={toolButtonClass}>
          <Users className="h-5 w-5" />
          <span>Team</span>
        </button>

        <button type="button" onClick={() => onAddNode('llm')} className={toolButtonClass}>
          <Cpu className="h-5 w-5" />
          <span>LLM</span>
        </button>
      </div>
    </div>
  );
};
